import { HALO_PRICING_RELEASE_APPROVED, HALO_WEBSITE_LIST_PRICE } from "./haloStorefront";

/**
 * Fortress storefront — three states per edition:
 *   closed  sales off, no launch capture ("Coming soon")
 *   notify  sales off, consented launch-notify capture ("Notify me")
 *   buy     sales open on the website (Stripe via the consumer platform)
 *
 * A sale opens only when BOTH source-owned HALO_PRICING_RELEASE_APPROVED is true
 * AND the consumer-platform catalog reports the verified, enabled USD products.
 * Environment variables are never consulted for the sale state, so env alone
 * can never open a Halo or Fortress sale. Defaults: closed (sales off).
 */
export const FORTRESS_SALE_STATES = Object.freeze({
  closed: "closed",
  notify: "notify",
  buy: "buy",
});

// Source-owned. Launch-notify only records a consented, one-off launch email.
// It never opens a sale or a checkout.
export const FORTRESS_LAUNCH_NOTIFY_ENABLED = false;

// Source-owned. No App Store Connect URL is approved yet; keep this false until
// the live Mac App Store listing exists. Never invent an ASC link.
export const FORTRESS_MAC_APP_STORE_RELEASE_APPROVED = false;

// Consumer-platform catalog keys (lightningflow-consumer-platform
// catalog/repository.py). Fortress is the Halo subscription.
export const HALO_CATALOG_KEYS = Object.freeze({ monthly: "halo-monthly", annual: "halo-annual" });
export const HALO_ENTITLEMENT_CODE = "halo";
// Fortress Pro is the SecureShield Pro one-time upgrade; the platform applies the
// server-side Halo-subscriber coupon so an active Halo customer pays US$11.99.
export const FORTRESS_PRO_ADDON_CATALOG_KEY = "securemac-pro";

export const FORTRESS_PRO_ADDON_PRICE = Object.freeze({
  currency: "USD",
  label: "US$11.99",
  cents: 1199,
  standaloneLabel: "US$19.99",
  standaloneCents: 1999,
  savingPercent: 40,
});

const HALO_CATALOG_SPEC = {
  monthly: { key: HALO_CATALOG_KEYS.monthly, interval: "month", cents: HALO_WEBSITE_LIST_PRICE.monthlyCents },
  annual: { key: HALO_CATALOG_KEYS.annual, interval: "year", cents: HALO_WEBSITE_LIST_PRICE.annualCents },
};

const safeAppStoreUrl = (value) => {
  if (!value) return "";
  try {
    const url = new URL(String(value).trim());
    if (url.protocol !== "https:" || url.hostname !== "apps.apple.com" || url.username || url.password || url.hash) return "";
    return url.toString();
  } catch {
    return "";
  }
};

const catalogProduct = (catalog, key) => {
  const products = Array.isArray(catalog?.products) ? catalog.products : [];
  return products.find((product) => product?.key === key) || null;
};

const verified = (product) => Boolean(product)
  && product.enabled === true
  && product.verification_status === "ready"
  && String(product.currency || "").toLowerCase() === "usd";

/** Both Halo plans must be enabled, verified and at the approved USD list. */
export function haloCatalogEnabled(catalog) {
  if (catalog?.ready !== true) return false;
  return Object.values(HALO_CATALOG_SPEC).every((spec) => {
    const product = catalogProduct(catalog, spec.key);
    return verified(product)
      && product.purchase_mode === "subscription"
      && product.billing_interval === spec.interval
      && product.unit_amount === spec.cents
      && product.entitlement_code === HALO_ENTITLEMENT_CODE;
  });
}

/** The SecureShield Pro product that carries the US$11.99 Halo-subscriber add-on. */
export function fortressProAddonCatalogEnabled(catalog) {
  if (catalog?.ready !== true) return false;
  const product = catalogProduct(catalog, FORTRESS_PRO_ADDON_CATALOG_KEY);
  return verified(product)
    && product.purchase_mode === "one_time"
    && product.unit_amount === FORTRESS_PRO_ADDON_PRICE.standaloneCents;
}

/** Halo pricing release approval AND a verified catalog. Never env. */
export function haloSaleOpen(catalog) {
  return HALO_PRICING_RELEASE_APPROVED === true && haloCatalogEnabled(catalog);
}

const closedState = () => (FORTRESS_LAUNCH_NOTIFY_ENABLED ? FORTRESS_SALE_STATES.notify : FORTRESS_SALE_STATES.closed);

function edition({ key, name, slug, open, checkout }) {
  const state = open ? FORTRESS_SALE_STATES.buy : closedState();
  return {
    key,
    name,
    slug,
    state,
    live: open,
    purchasable: open,
    notify: state === FORTRESS_SALE_STATES.notify,
    checkout: open ? checkout : null,
    // Website purchases start from the signed-in account, never a direct URL.
    href: open ? `/account?purchase=${slug}` : "",
  };
}

export function buildFortressStorefront(env = process.env, catalog = null) {
  const haloOpen = haloSaleOpen(catalog);
  const proOpen = haloOpen && fortressProAddonCatalogEnabled(catalog);
  const macAppStoreUrl = FORTRESS_MAC_APP_STORE_RELEASE_APPROVED && HALO_PRICING_RELEASE_APPROVED
    ? safeAppStoreUrl(env?.REACT_APP_FORTRESS_APPSTORE_URL)
    : "";
  return {
    list_currency: "USD",
    halo_open: haloOpen,
    base: edition({
      key: "base",
      name: "Fortress",
      slug: "halo-fortress",
      open: haloOpen,
      checkout: { kind: "halo", plans: ["monthly", "annual"] },
    }),
    pro: {
      ...edition({
        key: "pro",
        name: "Fortress Pro",
        slug: "halo-fortress-pro",
        open: proOpen,
        checkout: { kind: "fortress-pro-addon", requires: "halo", price: FORTRESS_PRO_ADDON_PRICE.label },
      }),
      addon: FORTRESS_PRO_ADDON_PRICE,
    },
    channels: {
      website: { name: "LightningFlow website", processor: "stripe", open: haloOpen },
      mac_app_store: {
        name: "Mac App Store",
        // Placeholder until App Store Connect publishes the listing.
        live: Boolean(macAppStoreUrl),
        url: macAppStoreUrl,
      },
    },
  };
}

export const fortressStorefront = buildFortressStorefront();

export function fortressCtaLabel(state) {
  if (state === FORTRESS_SALE_STATES.buy) return "Buy now";
  if (state === FORTRESS_SALE_STATES.notify) return "Notify me";
  return "Coming soon";
}
